import { Box, SxProps, Theme } from '@mui/material';
import { KeyboardEvent as ReactKeyboardEvent, useState } from 'react';
import {
    formatOnScale,
    IntensityScale,
    roundOnScale,
    STORED_INTENSITY_SCALE,
} from '~/domain/dicom/intensityScale';
import { withUnit } from '~/domain/dicom/intensityUnits';
import {
    isAcceptedWindowCenter,
    isAcceptedWindowWidth,
    WindowLevelBounds,
} from '~/domain/patient/windowLevel';
import EditableNumericValue from '~/components/EditableNumericValue';
import { useVerticalNudge } from './useVerticalNudge';

/** Which half of a window/level pair a field edits. */
export type WindowLevelField = 'window' | 'center';

interface EditableWindowLevelValueProps {
    /** The stored value. */
    readonly value: number;
    readonly field: WindowLevelField;
    /** The series' level range, which decides what the field accepts. */
    readonly bounds: WindowLevelBounds;
    /**
     * The scale the value is shown and typed on. The stored intensity
     * itself unless the series is read in a unit of its own, as a PET is.
     */
    readonly scale?: IntensityScale;
    /**
     * The unit the series' values are in, '' when they carry none. Named
     * with the least width the field accepts, even where the value itself
     * is shown bare.
     */
    readonly valueUnit: string;
    /** Applied once per drag step, positive when the pointer moves up. */
    readonly onNudge: (delta: number) => void;
    /** A typed value that passed the range check, as the stored intensity. */
    readonly onCommit: (value: number) => void;
    /**
     * Called when a typed window width is below the least width the field
     * accepts, with that minimum on the field's scale and in
     * {@link EditableWindowLevelValueProps.valueUnit}. The typed width is
     * not applied.
     */
    readonly onWidthBelowMinimum?: (minimum: string) => void;
    /** Styling for the display element, so each surface keeps its own chrome. */
    readonly sx?: SxProps<Theme>;
    readonly testId?: string;
}

const DISPLAY_SX = {
    cursor: 'ns-resize',
    userSelect: 'none',
    '&:hover': { textDecoration: 'underline' },
} as const;

/** The value while a drag holds it, so the hand on it is visible. */
const DRAGGING_SX = { opacity: 0.7, fontWeight: 'bold' } as const;

/**
 * One editable half of a window/level pair: drag it vertically to nudge, or
 * click it to type a value.
 *
 * The value is shown and typed on its scale, and a typed value is converted
 * back to the stored intensity it names. A typed value outside the series'
 * range, or finer than the scale shows, reverts to the previous one, and a
 * width below the least one the field accepts is reported through
 * {@link EditableWindowLevelValueProps.onWidthBelowMinimum} so the caller
 * can say why. The field never clamps a clinical value in silence. Writes
 * leave through
 * {@link EditableWindowLevelValueProps.onNudge} and
 * {@link EditableWindowLevelValueProps.onCommit}, so the caller decides where
 * the value goes.
 */
export default function EditableWindowLevelValue({
    value,
    field,
    bounds,
    scale = STORED_INTENSITY_SCALE,
    valueUnit,
    onNudge,
    onCommit,
    onWidthBelowMinimum,
    sx,
    testId,
}: EditableWindowLevelValueProps) {
    const [isEditing, setIsEditing] = useState(false);
    const { onMouseDown, isDragging } = useVerticalNudge({
        onNudge,
        onClick: () => setIsEditing(true),
        disabled: isEditing,
    });

    const isWindow = field === 'window';
    const ariaLabel = isWindow ? 'Window width' : 'Window center';
    const step = 10 ** -scale.decimals;

    // Shown and edited at the scale's precision. The series may carry a
    // finer stored value; it is untouched until the user commits a number
    // of their own.
    const shownValue = roundOnScale(scale, value);
    // One stored unit is the least width the field accepts, shown on the
    // scale and never finer than the scale's own step.
    const minimumWidth = Math.max(step, roundOnScale(scale, 1));
    const minimumWidthText = withUnit(
        minimumWidth.toFixed(scale.decimals),
        valueUnit
    );

    if (isEditing) {
        return (
            <EditableNumericValue
                value={shownValue}
                onCommit={(typed) => onCommit(scale.toStored(typed))}
                validate={(candidate) =>
                    isWindow
                        ? isAcceptedWindowWidth(candidate, bounds, scale)
                        : isAcceptedWindowCenter(candidate, bounds, scale)
                }
                onReject={(rejected) => {
                    if (isWindow && rejected < minimumWidth) {
                        onWidthBelowMinimum?.(minimumWidthText);
                    }
                }}
                min={
                    isWindow
                        ? minimumWidth
                        : roundOnScale(scale, bounds.minLevel)
                }
                max={roundOnScale(
                    scale,
                    isWindow
                        ? bounds.maxLevel - bounds.minLevel
                        : bounds.maxLevel
                )}
                step={step}
                ariaLabel={ariaLabel}
                testId={testId}
                onEditEnd={() => setIsEditing(false)}
                defaultEditing
            />
        );
    }

    return (
        <Box
            component="span"
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            data-testid={testId}
            onMouseDown={onMouseDown}
            // The drag is a pointer gesture; the keyboard goes straight to
            // typing, the same way the editor it opens is reached elsewhere.
            onKeyDown={(event: ReactKeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsEditing(true);
                }
            }}
            sx={[
                DISPLAY_SX,
                isDragging && DRAGGING_SX,
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {formatOnScale(scale, value)}
        </Box>
    );
}
